import { createContext, useContext, useEffect, useState, ReactNode } from "react"
import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client"
import { authClient } from "./auth"
import { FeatureResult, GetPreferencesDocument, SetActiveOrganizationDocument } from "@hydrologiq/gql-experience/graphql"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"

export interface User {
  id: string
  email: string
  name: string
  role?: "user" | "admin" | "operator" | "readonly"
  emailVerified: boolean
  createdAt: Date
  updatedAt: Date
}

interface Session {
  user: User
  session: {
    token: string
    expiresAt: Date
  }
}

export interface Organization {
  id: string
  name: string
  slug: string
  logo?: string | null
  createdAt: Date
  metadata?: Record<string, unknown>
  role?: string | null
  memberId?: string | null
  memberCreatedAt?: string | null
}

export interface Member {
  id: string
  organizationId: string
  userId: string
  email: string
  role: string
  createdAt: Date
}

export interface ActiveOrganization {
  organization: Organization
  member: Member
}

interface AuthContextValue {
  user: User | null
  session: Session | null
  loading: boolean
  signingOut: boolean
  signOut: () => Promise<void>
  // Organization management
  organizations: Organization[]
  activeOrganization: ActiveOrganization | null
  switchOrganization: (organizationId: string) => Promise<void>
  refreshOrganizations: () => Promise<void>
  features: FeatureResult[]
  preferredSection: string | null
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider = ({ children, loader }: { children: ReactNode; loader: ReactNode }) => {
  const apolloClient = useApolloClient()
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [signingOut, setSigningOut] = useState(false)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [activeOrganization, setActiveOrgState] = useState<ActiveOrganization | null>(null)
  const [profile, setProfile] = useState<string | null>(null)
  const [features, setFeatures] = useState<FeatureResult[]>([])
  const [preferredSection, setPreferredSection] = useState<string | null>(null)

  const [fetchPreferences] = useLazyQuery(GetPreferencesDocument, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.getPreferences?.organizations) {
        setOrganizations(
          data.getPreferences.organizations
            .filter((org): org is NonNullable<typeof org> => org !== null)
            .map((org) => ({
              ...org,
              createdAt: new Date(org.createdAt),
            })) as Organization[]
        )
      }
      if (data?.getPreferences?.profile) {
        setProfile(data.getPreferences.profile)
      }
      if (data?.getPreferences?.features) {
        setFeatures(data.getPreferences.features)
      }
      if (data?.getPreferences?.feature) {
        setPreferredSection(data.getPreferences.feature)
      } else {
        setPreferredSection(null)
      }
    },
    onError: (error) => {
      console.error("Failed to fetch preferences:", error)
    },
  })

  const [setActiveOrganizationMutation] = useMutation(SetActiveOrganizationDocument, {
    refetchQueries: [GetPreferencesDocument],
    awaitRefetchQueries: true,
  })

  const refreshOrganizations = async () => {
    try {
      await fetchPreferences()
    } catch (error) {
      console.error("Failed to load organizations:", error)
    }
  }

  const loadActiveOrganization = async () => {
    try {
      // Get the active organization ID and organizations list from user preferences
      const { data } = await fetchPreferences()
      const preferences = data?.getPreferences
      const organizationId = preferences?.organization
      const organizations = preferences?.organizations

      if (!organizationId || !organizations) {
        setActiveOrgState(null)
        return
      }

      // Find the organization in the list
      const org = organizations.find((o: any) => o.id === organizationId) as any
      if (org && org.role) {
        // Get user info from session for member email
        const sessionData = await authClient.getSession()
        const userEmail = sessionData?.data?.user?.email || ""

        setActiveOrgState({
          organization: { ...org, createdAt: new Date(org.createdAt) },
          member: {
            id: org.memberId || "",
            organizationId: org.id,
            userId: sessionData?.data?.user?.id || "",
            email: userEmail,
            role: org.role,
            createdAt: org.memberCreatedAt ? new Date(org.memberCreatedAt) : new Date(org.createdAt),
          },
        })
        return
      }

      setActiveOrgState(null)
    } catch (error) {
      console.error("Failed to load active organization:", error)
      setActiveOrgState(null)
    }
  }

  useEffect(() => {
    // Get initial session
    const initSession = async () => {
      try {
        const sessionData = await authClient.getSession()
        if (sessionData?.data) {
          setUser(sessionData.data.user as User)
          setSession(sessionData.data as Session)

          // Load organizations and active organization
          if (!activeOrganization || !organizations)
            await Promise.all([refreshOrganizations(), loadActiveOrganization()])
        } else {
          // No valid session found, ensure bearer token is cleared
          console.log("No valid session found, clearing bearer token")
          localStorage.removeItem("bearer_token")
        }
      } catch (error) {
        console.error("Failed to get session:", error)
        // Clear bearer token on session errors
        localStorage.removeItem("bearer_token")
      } finally {
        setLoading(false)
      }
    }

    initSession()

    // Set up periodic session validation to catch expired tokens
    const sessionCheckInterval = setInterval(async () => {
      if (user) initSession()
    }, 10 * 60 * 1000) // Check every 10 minutes

    // Cleanup interval on unmount
    return () => {
      clearInterval(sessionCheckInterval)
    }

    // TODO: Listen for session changes when better-auth supports it
    // For now, we'll rely on the initial session check and periodic validation
  }, [])

  const signOut = async () => {
    if (signingOut) return // Prevent multiple simultaneous sign-out attempts

    setSigningOut(true)
    try {
      await authClient.signOut()
      try {
        await apolloClient.clearStore()
      } catch (e) {
        console.warn("Failed to clear Apollo store on signout:", e)
      }
      setUser(null)
      setSession(null)
      setOrganizations([])
      setActiveOrgState(null)
      // Clear bearer token from localStorage
      localStorage.removeItem("bearer_token")
      toaster.success({
        title: "Signed out",
        description: "You have been successfully signed out",
      })
    } catch (error: any) {
      console.error("Failed to sign out:", error)
      toaster.error({
        title: "Sign out failed",
        description: error.message || "An error occurred while signing out",
      })
      throw error
    } finally {
      setSigningOut(false)
    }
  }

  const switchOrganization = async (organizationId: string) => {
    try {
      // Update user preferences via GraphQL mutation
      // The mutation will automatically refetch GetPreferencesDocument
      const result = await setActiveOrganizationMutation({
        variables: { organizationId },
      })

      // Verify the mutation succeeded
      if (result.data?.setActiveOrganization) {
        // Clear Apollo Client in-memory cache to prevent cross-tenant data leak
        try {
          await apolloClient.clearStore()
        } catch (e) {
          console.warn("Failed to clear Apollo store on org switch:", e)
        }

        const org = organizations.find((o: any) => o.id === organizationId) as any
        if (org && org.role) {
          // Get user info from session for member email
          const sessionData = await authClient.getSession()
          const userEmail = sessionData?.data?.user?.email || ""

          setActiveOrgState({
            organization: { ...org, createdAt: new Date(org.createdAt) },
            member: {
              id: org.memberId || "",
              organizationId: org.id,
              userId: sessionData?.data?.user?.id || "",
              email: userEmail,
              role: org.role,
              createdAt: org.memberCreatedAt ? new Date(org.memberCreatedAt) : new Date(org.createdAt),
            },
          })
        }

        // Re-fetch preferences to populate features and profile for the new active org
        await fetchPreferences()
      } else {
        throw new Error("Failed to switch organization")
      }
    } catch (error) {
      console.error("Failed to switch organization:", error)
      throw error
    }
  }

  const value: AuthContextValue = {
    user,
    session,
    loading,
    signingOut,
    signOut,
    organizations,
    activeOrganization,
    switchOrganization,
    refreshOrganizations,
    features,
    preferredSection,
  }

  return (
    <AuthContext.Provider value={value}>
      {loading ? loader : children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export const useUser = () => {
  const { user } = useAuth()
  return user
}

export const useSession = () => {
  const { session } = useAuth()
  return session
}

export const useOrganizations = () => {
  const { organizations, refreshOrganizations } = useAuth()
  return { organizations, refreshOrganizations }
}

export const useActiveOrganization = () => {
  const { activeOrganization, switchOrganization, features, preferredSection } = useAuth()
  return { activeOrganization, switchOrganization, features, preferredSection }
}
